<script lang="ts">
  import { tick, createEventDispatcher } from 'svelte';
  import { toast } from '../js/toast.js';
  import { api } from '../js/api.js';
  import { auth } from '../js/auth.js';
  import { spinner } from '../js/spinner.js';
  import { loadStripeScript } from '../js/stripe-loader.js';
  import Modal from './Modal.svelte';

  export let showModal = false;
  
  const dispatch = createEventDispatcher();

  let stripe: any;
  let elements: any;
  let cardNumber: any;
  let cardExpiry: any;
  let cardCvc: any;
  let cardholderName = '';
  let isProcessing = false;
  let modalWidth = 420;

  const stripePublishableKey = typeof import.meta.env.VITE_STRIPE_KEY === 'string' ? (import.meta.env.VITE_STRIPE_KEY || '').trim() : '';

  // Load Stripe only when modal is shown
  $: if (showModal && stripePublishableKey) {
    loadStripeScript().then((StripeFn) => {
      if (!StripeFn) return;
      if (!stripe) {
        stripe = StripeFn(stripePublishableKey);
        elements = stripe.elements();
      }
      tick().then(() => setupCardElements());
    });
  }

  function setupCardElements() {
    if (!stripe || !elements) return;
    // Clean up existing Stripe elements before creating new ones
    if (cardNumber) {
      cardNumber.destroy();
      cardNumber = null;
    }
    if (cardExpiry) {
      cardExpiry.destroy();
      cardExpiry = null;
    }
    if (cardCvc) {
      cardCvc.destroy();
      cardCvc = null;
    }

    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Poppins", "Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    };

    cardNumber = elements.create('cardNumber', { style });
    cardNumber.mount('#add-card-number');
    cardExpiry = elements.create('cardExpiry', { style });
    cardExpiry.mount('#add-card-expiry');
    cardCvc = elements.create('cardCvc', { style });
    cardCvc.mount('#add-card-cvc');
  }

  function resetForm() {
    cardholderName = '';
  }

  function handleClose() {
    resetForm();
    showModal = false;
  }

  async function handleSubmit() {
    if (!stripe) {
      toast.showNotification(false, 'Payment is not configured. Please set VITE_STRIPE_KEY in your environment.');
      return;
    }
    if (!cardholderName.trim()) {
      toast.showNotification(false, 'Please enter the cardholder name');
      return;
    }

    isProcessing = true;
    
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber,
        billing_details: { name: cardholderName },
      });

      if (error) {
        toast.showNotification(false, error.message);
        console.error(error);
        return;
      }

      spinner.show();
      const response = await api.addCard(($auth as any).user.uuid, paymentMethod.id) as any;
      spinner.hide();

      if (response.success) {
        toast.showNotification(true, 'Card added successfully');
        dispatch('added');
        handleClose();
      } else {
        toast.showNotification(false, response.message || 'Failed to add card');
      }
    } catch (err) {
      console.error('Error adding card:', err);
      toast.showNotification(false, 'Error adding card');
      spinner.hide();
    } finally {
      isProcessing = false;
    }
  }
</script>

<Modal bind:showModal bind:modalWidth>
  <div slot="header">Add Payment Method</div>
  <div slot="body">
    <p class="modal-description">
      Add a credit or debit card to your account.
    </p>
    <form on:submit|preventDefault={handleSubmit}>
      <div class="form-field">
        <label for="add-cardholder-name" class="field-label">Name on Card</label>
        <input
          id="add-cardholder-name"
          type="text"
          class="field-input"
          bind:value={cardholderName}
          placeholder="John Smith"
          required
        />
      </div>
      <div class="form-field">
        <span class="field-label" id="card-number-label">Card Number</span>
        <div id="add-card-number" class="stripe-input" aria-labelledby="card-number-label"></div>
      </div>
      <div class="form-row">
        <div class="form-field half">
          <span class="field-label" id="card-expiry-label">Expiry Date</span>
          <div id="add-card-expiry" class="stripe-input" aria-labelledby="card-expiry-label"></div>
        </div>
        <div class="form-field half">
          <span class="field-label" id="card-cvc-label">CVC</span>
          <div id="add-card-cvc" class="stripe-input" aria-labelledby="card-cvc-label"></div>
        </div>
      </div>
    </form>
  </div>
  <div slot="footer">
    <div class="modal-actions">
      <button class="cancel-button" on:click|preventDefault={handleClose} disabled={isProcessing}>
        Cancel
      </button>
      <button class="save-button" on:click|preventDefault={handleSubmit} disabled={isProcessing}>
        {isProcessing ? 'Adding...' : 'Add Card'}
      </button>
    </div>
  </div>
</Modal>

<style>
  .modal-description {
    color: var(--grey-secondary);
    margin: 0 0 1.5rem;
    line-height: 1.5;
    font-size: 0.9375rem;
  }

  .form-field {
    margin-bottom: 1rem;
  }

  .form-row {
    display: flex;
    gap: 1rem;
  }

  .form-field.half {
    flex: 1;
  }

  .field-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--grey);
    margin-bottom: 0.5rem;
  }

  .field-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 1rem;
    background-color: var(--white);
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }

  .field-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .stripe-input {
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background-color: var(--white);
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .stripe-input:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }

  .cancel-button {
    padding: 0.625rem 1.25rem;
    background: transparent;
    border: 1px solid var(--border-color);
    color: var(--grey);
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;
  }

  .cancel-button:hover:not(:disabled) {
    background: var(--greyish);
    border-color: var(--grey-secondary);
  }

  .cancel-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .save-button {
    padding: 0.625rem 1.25rem;
    background: #d91b42;
    color: var(--white);
    border: 0;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
    transition: background-color 0.2s;
  }

  .save-button:hover:not(:disabled) {
    background: #b81535;
  }

  .save-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
